import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet, Landmark, TrendingUp, TrendingDown, ArrowLeftRight, MessageSquare, ClipboardList,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { AccountingLayout } from '../components/AccountingLayout';
import { dashboardSummary } from '../lib/api';
import { formatRp } from '../lib/format';

const StatCard = ({ label, value, icon: Icon, tone = 'stone', testid }) => {
  const tones = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100' },
    rose:    { bg: 'bg-rose-50',    text: 'text-rose-600',    border: 'border-rose-100' },
    sky:     { bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-100' },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-100' },
  };
  const t = tones[tone] || tones.emerald;
  return (
    <div className="rounded-xl border border-stone-200 bg-white px-6 py-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
          {label}
        </div>
        <div className={`p-2 rounded-lg ${t.bg} ${t.border} border`}>
          <Icon className={`w-4 h-4 ${t.text}`} />
        </div>
      </div>
      <div className="text-3xl font-semibold text-stone-900 mt-3 font-mono">
        {formatRp(value)}
      </div>
    </div>
  );
};

const monthsShort = (m) => {
  const map = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  const [y, mo] = m.split('-');
  return `${map[parseInt(mo, 10) - 1]} ${y.slice(2)}`;
};

const formatDateShort = (iso) => {
  if (!iso || iso.length < 10) return '—';
  const [y, m, d] = iso.split('-');
  const map = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  return `${parseInt(d, 10)} ${map[parseInt(m, 10) - 1]} ${y}`;
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await dashboardSummary();
        setData(r);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <AccountingLayout>
        <div className="text-sm text-stone-500 py-20 text-center">Memuat ringkasan…</div>
      </AccountingLayout>
    );
  }
  if (!data) {
    return (
      <AccountingLayout>
        <div className="text-sm text-rose-500 py-20 text-center">Gagal memuat dasbor.</div>
      </AccountingLayout>
    );
  }

  const chartData = (data.trend || []).map((t) => ({
    month: t.month,
    Pendapatan: Number(t.revenue) || 0,
    Beban: Number(t.expense) || 0,
  }));

  const netIsNeg = (data.net_income || 0) < 0;

  return (
    <AccountingLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">
            Dasbor
          </h1>
          <p className="text-sm text-stone-500 mt-2">Ringkasan kondisi keuangan Anda saat ini</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard label="Total Aset"       value={data.total_assets}      icon={Wallet}       tone="emerald" testid="stat-assets" />
          <StatCard label="Total Kewajiban"  value={data.total_liabilities} icon={Landmark}     tone="rose"    testid="stat-liab" />
          <StatCard label="Total Pendapatan" value={data.total_revenue}     icon={TrendingUp}   tone="sky"     testid="stat-revenue" />
          <StatCard label="Total Beban"      value={data.total_expense}     icon={TrendingDown} tone="amber"   testid="stat-expense" />
        </div>

        {/* Shortcut: Stock Opname */}
        <Link to="/stock-opname"
          className="block rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-5 hover:shadow-sm transition"
          data-testid="dashboard-opname-shortcut">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-lg bg-emerald-800 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-emerald-900">Stock Opname</div>
              <div className="text-xs text-stone-600">Hitung ulang persediaan fisik toko dan bandingkan dengan stok sistem.</div>
            </div>
            <div className="text-xs font-medium text-emerald-800">Buka →</div>
          </div>
        </Link>

        {/* Chart + Net income */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-6">
          <div className="rounded-xl border border-stone-200 bg-white p-5" data-testid="chart-card">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-stone-900">Pendapatan vs Beban</div>
              <div className="text-xs text-stone-500">6 bulan terakhir</div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#78716c' }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#78716c' }}
                    axisLine={false} tickLine={false}
                    tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                  />
                  <Tooltip
                    formatter={(v) => formatRp(v)}
                    contentStyle={{ borderRadius: 8, borderColor: '#e7e5e4', fontSize: 12 }}
                    labelStyle={{ color: '#44403c', fontWeight: 600 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                  <Bar dataKey="Beban"      fill="#dc2626" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Pendapatan" fill="#065f46" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border border-stone-200 bg-white p-5" data-testid="net-card">
            <div className="flex items-center gap-2 text-emerald-800 mb-1">
              <ArrowLeftRight className="w-4 h-4" />
              <div className="text-sm font-semibold">Laba Bersih</div>
            </div>
            <div className={`text-4xl font-semibold font-mono mt-3 mb-6 ${netIsNeg ? 'text-rose-600' : 'text-emerald-700'}`}>
              {netIsNeg ? '-' : ''}{formatRp(Math.abs(data.net_income || 0))}
            </div>
            <div className="text-xs text-stone-500 -mt-3 mb-4">Tahun berjalan (YTD)</div>
            <div className="border-t border-stone-100 pt-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">Jumlah Jurnal</span>
                <span className="text-stone-900 font-semibold" data-testid="stat-journal-count">
                  {data.journal_count}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">Jumlah Akun</span>
                <span className="text-stone-900 font-semibold" data-testid="stat-account-count">
                  {data.account_count}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent journals */}
        <div className="rounded-xl border border-stone-200 bg-white" data-testid="recent-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-emerald-800" />
              <div className="text-lg font-semibold text-stone-900">Jurnal Terbaru</div>
            </div>
            <Link
              to="/jurnal"
              data-testid="link-all-journals"
              className="text-sm text-emerald-800 hover:text-emerald-900 font-medium"
            >
              Lihat semua
            </Link>
          </div>
          {(data.recent_journals || []).length === 0 ? (
            <div className="py-12 text-center text-sm text-stone-500">Belum ada jurnal.</div>
          ) : (
            <div>
              {data.recent_journals.map((j) => (
                <div
                  key={j.id}
                  data-testid={`recent-journal-${j.id}`}
                  className="flex items-center justify-between px-5 py-4 border-b border-stone-100 last:border-0 hover:bg-stone-50/40"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-stone-900 truncate">
                      {j.description || '(tanpa deskripsi)'}
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      {formatDateShort(j.date)}
                      {j.ref ? <> · <span className="font-mono">{j.ref}</span></> : null}
                    </div>
                  </div>
                  <div className="text-sm font-mono font-semibold text-stone-900 shrink-0 ml-4">
                    {formatRp(j.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AccountingLayout>
  );
}
